import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, Loader2, BadgeCheck, ThumbsUp, ThumbsDown, LifeBuoy, Zap } from "lucide-react";
import api, { gbp, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const AREA_LABELS = [["/checkout", "Checkout helper"], ["/order-confirmation", "Checkout helper"], ["/vendor", "Seller helper"], ["/sell", "Seller helper"], ["/account", "Orders helper"], ["/track", "Orders helper"], ["/login", "Account helper"], ["/register", "Account helper"]];
const helperLabel = () => (AREA_LABELS.find(([p]) => window.location.pathname.startsWith(p)) || [null, "Shopping helper"])[1];

function getSessionId() {
  let s = localStorage.getItem("mk_chat_session");
  if (!s) { s = "chat_" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("mk_chat_session", s); }
  return s;
}

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm MRQK, your shopping assistant 👋 Ask me to find products, or about delivery, returns & payments." },
  ]);
  const [products, setProducts] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(getSessionId());
  const bottomRef = useRef(null);
  const [suggestions, setSuggestions] = useState(["Recommend a gift under £50", "What's your returns policy?", "Show me electronics"]);
  const [handoff, setHandoff] = useState(false);
  const [handoffEmail, setHandoffEmail] = useState("");
  const [handoffDone, setHandoffDone] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/faqs").then((r) => {
      const qs = (r.data || []).map((f) => f.question).slice(0, 4);
      if (qs.length) setSuggestions(qs);
    }).catch(() => {});
  }, []);

  useEffect(() => { if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open, loading]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "", streaming: true }]);
    setInput("");
    setProducts([]);
    setLoading(true);
    try {
      const token = localStorage.getItem("mk_token");
      const res = await fetch(`${API}/assistant/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(localStorage.getItem("mk_sandbox") === "1" ? { "X-Sandbox": "1" } : {}) },
        body: JSON.stringify({ message: text, session_id: sessionId.current, page: window.location.pathname }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let failed = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.type === "delta") {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + ev.content };
              return copy;
            });
          } else if (ev.type === "done") {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], streaming: false, source: ev.source, kb_id: ev.kb_id };
              return copy;
            });
            setProducts(ev.products || []);
            if (ev.handoff) setHandoff(true);
          } else if (ev.type === "error") {
            failed = true;
          }
        }
      }
      if (failed) throw new Error("assistant error");
    } catch {
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last?.role === "assistant" && !last.content) {
          copy[copy.length - 1] = { role: "assistant", content: "Sorry, I'm having trouble right now. Please try again in a moment." };
        }
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  const feedback = async (i, helpful) => {
    const question = messages[i - 1]?.role === "user" ? messages[i - 1].content : "";
    setMessages((m) => m.map((msg, idx) => (idx === i ? { ...msg, fb: helpful ? "up" : "down" } : msg)));
    try {
      if (messages[i].kb_id) await api.post("/assistant/kb-feedback", { kb_id: messages[i].kb_id, helpful });
      else await api.post("/assistant/feedback", { question, answer: messages[i].content, helpful });
    } catch {}
    if (!helpful) setHandoff(true);
  };

  const requestHuman = async () => {
    const email = (user?.email || handoffEmail || "").trim();
    if (!email.includes("@")) return toast.error("Add your email so a person can reply");
    try {
      const { data } = await api.post("/assistant/handoff", { session_id: sessionId.current, email, name: user?.name || "", page: window.location.pathname });
      setHandoffDone(data.ticket_id);
      setMessages((m) => [...m, { role: "assistant", content: `Done — a real person has your whole conversation and will reply to ${email}. Reference ${data.ticket_id}.`, source: "handoff" }]);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't hand over — please use /contact"); }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="chat-open-btn"
          className="fixed bottom-5 left-5 z-40 w-14 h-14 bg-[#0047AB] text-white flex items-center justify-center shadow-2xl hover:bg-[#003380] transition-colors group">
          <MessageCircle className="w-6 h-6" strokeWidth={1.75} />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-[#E63946] rounded-full animate-pulse" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-5 left-5 z-40 w-[calc(100vw-2.5rem)] sm:w-96 h-[560px] max-h-[80vh] bg-white border border-neutral-200 shadow-2xl flex flex-col" data-testid="chat-panel">
          <div className="bg-[#0A0A0A] text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#0047AB] flex items-center justify-center"><Sparkles className="w-4 h-4" /></div>
              <div><p className="font-heading font-medium leading-none">MRQK Assistant</p><span className="text-[10px] text-neutral-400 uppercase tracking-wider" data-testid="chat-helper-label">{helperLabel()} · learns from solved issues</span></div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close-btn" className="hover:opacity-70"><X className="w-5 h-5" /></button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-neutral-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}`}>
                <div className={`max-w-[85%] ${m.role === "user" ? "" : "space-y-1"}`}>
                  <div className={`px-3 py-2 text-sm leading-relaxed ${m.role === "user" ? "bg-[#0047AB] text-white" : "bg-white border border-neutral-200"}`}>{m.content}{m.streaming && !m.content && <Loader2 className="w-4 h-4 animate-spin text-[#0047AB] inline" />}{m.streaming && m.content && <span className="inline-block w-1.5 h-3.5 bg-[#0047AB] ml-0.5 animate-pulse align-middle" />}</div>
                  {m.source === "faq" && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-[#00875A] font-medium uppercase tracking-wide" data-testid="chat-faq-badge">
                      <BadgeCheck className="w-3 h-3" strokeWidth={2} />Answered from Help Centre
                    </span>
                  )}
                  {m.source === "kb" && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-[#0047AB] font-medium uppercase tracking-wide" data-testid="chat-kb-badge">
                      <Zap className="w-3 h-3" strokeWidth={2} />Proven fix · instant
                    </span>
                  )}
                  {m.role === "assistant" && !m.streaming && m.content && i > 0 && (
                    <div className="flex items-center gap-1.5" data-testid={`chat-feedback-${i}`}>
                      {m.fb ? (
                        <span className="text-[10px] text-neutral-400">{m.fb === "up" ? "Thanks — I'll remember that! ✓" : "Thanks — I'll do better."}</span>
                      ) : (
                        <>
                          <span className="text-[10px] text-neutral-300">Helpful?</span>
                          <button onClick={() => feedback(i, true)} className="p-0.5 text-neutral-300 hover:text-[#00875A]" data-testid={`chat-thumbs-up-${i}`}><ThumbsUp className="w-3 h-3" /></button>
                          <button onClick={() => feedback(i, false)} className="p-0.5 text-neutral-300 hover:text-[#E63946]" data-testid={`chat-thumbs-down-${i}`}><ThumbsDown className="w-3 h-3" /></button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {products.length > 0 && (
              <div className="space-y-2" data-testid="chat-products">
                {products.map((p) => (
                  <Link key={p.id} to={`/product/${p.slug}`} onClick={() => setOpen(false)} className="flex gap-3 items-center bg-white border border-neutral-200 p-2 hover:border-[#0047AB] transition-colors">
                    <div className="w-11 h-11 bg-neutral-100 shrink-0"><img src={p.image} alt="" className="w-full h-full object-cover" /></div>
                    <div className="flex-1 min-w-0"><p className="text-xs line-clamp-1 font-medium">{p.title}</p><p className="text-sm font-semibold">{gbp(p.price)}</p></div>
                  </Link>
                ))}
              </div>
            )}
            {(handoff || messages.length > 4) && !handoffDone && (
              <div className="border border-amber-300 bg-amber-50 p-3 text-xs space-y-2" data-testid="chat-handoff-box">
                <p className="font-medium flex items-center gap-1"><LifeBuoy className="w-3.5 h-3.5" />Want a real person? Your whole chat goes with it — no repeating yourself.</p>
                {!user && <input value={handoffEmail} onChange={(e) => setHandoffEmail(e.target.value)} placeholder="Your email for the reply" className="w-full border border-neutral-300 px-2 py-1.5 text-xs outline-none focus:border-[#0047AB]" data-testid="chat-handoff-email" />}
                <button onClick={requestHuman} className="bg-black text-white px-3 py-1.5 text-xs hover:bg-neutral-800" data-testid="chat-talk-to-human">Talk to a human</button>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {messages.length <= 1 && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5 bg-neutral-50">
              {suggestions.map((s) => (
                <button key={s} onClick={() => { setInput(s); }} className="text-[11px] border border-neutral-300 px-2 py-1 hover:border-[#0047AB] hover:text-[#0047AB] transition-colors" data-testid="chat-suggestion">{s}</button>
              ))}
            </div>
          )}

          <form onSubmit={send} className="p-3 border-t border-neutral-200 flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask anything…" className="flex-1 border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-[#0047AB]" data-testid="chat-input" />
            <button type="submit" disabled={loading} className="bg-[#0047AB] text-white px-3 hover:bg-[#003380] transition-colors disabled:opacity-50" data-testid="chat-send-btn"><Send className="w-4 h-4" /></button>
          </form>
        </div>
      )}
    </>
  );
};
